import { c as _c } from "react/compiler-runtime";
import { useNumberFormatter } from "@opensea/i18n";
import { Button } from "@opensea/next-ui-kit";
import { Flex, FlexColumn, SpaceBetween, TextBody, TextBodySkeleton } from "@opensea/ui-kit";
import { CheckCircleFilled, EnergySavingsLeaf } from "@opensea/ui-kit/icons";
import { useAccount } from "@opensea/wallet";
import { isAfter } from "date-fns";
import { useTranslations } from "next-intl";
import { useShallow } from "zustand/react/shallow";
import { StagePriceDisplay } from "@/components/collection/drops/components/StagePriceDisplay";
import { DropStatus, MAX_MINTABLE_MAX } from "@/components/collection/drops/constants";
import { useFormatStageDate } from "@/components/collection/drops/use-format-stage-date";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { mintScheduleRowClasses, mintStageIndicator, stageEligibilityTextClasses } from "../../styles";
import { useDropStore } from "../CollectionDropContext";
export function MintSchedule() {
  const $ = _c(46);
  const [activeStageIndex, status, maxSupply, stages, eligibleStageIndices, isLoadingEligibility, refetchEligibility] = useDropStore(useShallow(_temp));
  const format = useNumberFormatter();
  const formatStageDate = useFormatStageDate();
  const {
    authenticated,
    ready
  } = useAccount();
  const hasAllowlistStages = stages.filter(_temp2).length > 0;
  let t0;
  if ($[0] !== refetchEligibility) {
    t0 = () => {
      refetchEligibility();
    };
    $[0] = refetchEligibility;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const authenticatedRefetch = useAuthenticatedCallback(t0);
  const t = useTranslations("MintSchedule");
  if (!stages.length) {
    return null;
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("mintSchedule");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <TextBody className="font-mono uppercase" weight="semibold">{t1}</TextBody>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== authenticated || $[7] !== authenticatedRefetch || $[8] !== hasAllowlistStages || $[9] !== ready || $[10] !== t) {
    t3 = !authenticated && ready && hasAllowlistStages ? <Button onClick={authenticatedRefetch} size="sm" variant="tertiary">{t("viewEligibility")}</Button> : null;
    $[6] = authenticated;
    $[7] = authenticatedRefetch;
    $[8] = hasAllowlistStages;
    $[9] = ready;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <SpaceBetween className="mb-6 items-center">{t2}{t3}</SpaceBetween>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== activeStageIndex || $[16] !== authenticated || $[17] !== eligibleStageIndices || $[18] !== format || $[19] !== formatStageDate || $[20] !== hasAllowlistStages || $[21] !== isLoadingEligibility || $[22] !== maxSupply || $[23] !== ready || $[24] !== stages || $[25] !== status || $[26] !== t) {
    let t6;
    if ($[28] !== activeStageIndex || $[29] !== authenticated || $[30] !== eligibleStageIndices || $[31] !== format || $[32] !== formatStageDate || $[33] !== hasAllowlistStages || $[34] !== isLoadingEligibility || $[35] !== maxSupply || $[36] !== ready || $[37] !== stages.length || $[38] !== status || $[39] !== t) {
      t6 = (stage_0, index) => {
        const isActiveStage = stage_0.stageIndex === activeStageIndex && status === DropStatus.MINTING;
        const isEligible = !stage_0.stageIndex || eligibleStageIndices.includes(stage_0.stageIndex);
        const isPastStage = status === DropStatus.MINTING && isAfter(new Date(), new Date(stage_0.endTime)) || status === DropStatus.MINT_ENDED || status === DropStatus.MINTED_OUT;
        const maxMintable = stage_0.eligibleMaxTotalMintableByWallet ?? stage_0.maxTotalMintableByWallet;
        return <SpaceBetween asChild={true} className={mintScheduleRowClasses({
          isActiveStage
        })} key={stage_0.stageIndex}><li><Flex className="relative items-center gap-6">{index !== stages.length - 1 ? <div className="absolute top-1/2 left-3 z-0 mt-3 h-[76px] w-0 border-border-1 border-l" /> : null}<div className={mintStageIndicator({
                isPastStage,
                isActiveStage
              })}>{isPastStage ? <CheckCircleFilled fill="success-1" size={24} /> : null}{isActiveStage ? <EnergySavingsLeaf fill="success-1" size={16} /> : null}</div><FlexColumn><TextBody weight="semibold">{stage_0.label || t("publicStage")}</TextBody><TextBody className="text-text-secondary" size="sm">{isPastStage ? t("startedTime", {
                    date: formatStageDate(stage_0.startTime)
                  }) : t("startTime", {
                    date: formatStageDate(stage_0.startTime)
                  })}</TextBody>{index === stages.length - 1 ? <TextBody className="text-text-secondary" size="sm">{isPastStage ? t("endedTime", {
                    date: formatStageDate(stage_0.endTime)
                  }) : t("endTime", {
                    date: formatStageDate(stage_0.endTime)
                  })}</TextBody> : null}<TextBody className="font-mono text-text-secondary uppercase" size="xs"><StagePriceDisplay stage={stage_0} />{maxMintable !== maxSupply && maxMintable !== MAX_MINTABLE_MAX ? ` | ${t("mintLimit", {
                    maxMintable: format(maxMintable, {
                      display: "full",
                      custom: {
                        maximumFractionDigits: 0
                      }
                    })
                  })}` : null}</TextBody></FlexColumn></Flex>{!ready || isLoadingEligibility ? <TextBodySkeleton className="w-[60px]" size="xs" /> : authenticated || !hasAllowlistStages ? <TextBody className={stageEligibilityTextClasses({
              isEligible
            })} size="xs" weight="semibold">{isEligible ? t("eligible") : t("notEligible")}</TextBody> : null}</li></SpaceBetween>;
      };
      $[28] = activeStageIndex;
      $[29] = authenticated;
      $[30] = eligibleStageIndices;
      $[31] = format;
      $[32] = formatStageDate;
      $[33] = hasAllowlistStages;
      $[34] = isLoadingEligibility;
      $[35] = maxSupply;
      $[36] = ready;
      $[37] = stages.length;
      $[38] = status;
      $[39] = t;
      $[40] = t6;
    } else {
      t6 = $[40];
    }
    t5 = stages.map(t6);
    $[15] = activeStageIndex;
    $[16] = authenticated;
    $[17] = eligibleStageIndices;
    $[18] = format;
    $[19] = formatStageDate;
    $[20] = hasAllowlistStages;
    $[21] = isLoadingEligibility;
    $[22] = maxSupply;
    $[23] = ready;
    $[24] = stages;
    $[25] = status;
    $[26] = t;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[41] !== t5) {
    t6 = <ol className="space-y-10">{t5}</ol>;
    $[41] = t5;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== t4 || $[44] !== t6) {
    t7 = <FlexColumn>{t4}{t6}</FlexColumn>;
    $[43] = t4;
    $[44] = t6;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  return t7;
}
function _temp2(stage) {
  return stage.stageIndex;
}
function _temp(state) {
  return [state.activeStageIndex, state.status, state.maxSupply, state.stages, state.eligibleStageIndices, state.isLoadingEligibility, state.refetchEligibility];
}